import { createApi } from '@reduxjs/toolkit/query/react';
import {
  StaffRole,
  CreateStaffRoleDTO,
  UpdateStaffRoleDTO,
  StaffMember,
  CreateStaffMemberDTO,
  UpdateStaffMemberDTO,
  StaffDoor,
  CreateStaffDoorDTO,
  UpdateStaffDoorDTO,
  StaffScanRecord,
  StaffRegistrationLink,
  StaffRegistrationFormDTO,
  StaffReportFilter,
  StaffImportResult,
} from '../../models/staff';
import { Response } from '../../models/shared';
import baseQueryWithReauth from './base';

export const staffApi = createApi({
  reducerPath: 'staffApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['staff_roles', 'staff_members', 'staff_doors', 'staff_reports', 'staff_links'],
  endpoints: (build) => ({
    // --- Roles ---
    getStaffRoles: build.query<StaffRole[], { businessId: string; eventId: string }>({
      providesTags: ['staff_roles'],
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/roles`,
        method: 'GET',
      }),
      transformResponse: (response: Response<StaffRole[]>): StaffRole[] => response.data || [],
    }),
    createStaffRole: build.mutation<Response<StaffRole>, { businessId: string; eventId: string; data: CreateStaffRoleDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/roles`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['staff_roles'],
    }),
    updateStaffRole: build.mutation<Response<StaffRole>, { businessId: string; eventId: string; data: UpdateStaffRoleDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/roles/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['staff_roles'],
    }),
    deleteStaffRole: build.mutation<Response<null>, { businessId: string; eventId: string; roleId: string }>({
      query: ({ businessId, eventId, roleId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/roles/${roleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['staff_roles'],
    }),
    replicateStaffRoles: build.mutation<Response<StaffRole[]>, { businessId: string; eventId: string; sourceEventId: string }>({
      query: ({ businessId, eventId, sourceEventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/roles/replicate`,
        method: 'POST',
        body: { source_event_id: sourceEventId },
      }),
      invalidatesTags: ['staff_roles'],
    }),

    // --- Personal ---
    getStaffMembers: build.query<StaffMember[], { businessId: string; eventId: string }>({
      providesTags: ['staff_members'],
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members`,
        method: 'GET',
      }),
      transformResponse: (response: Response<StaffMember[]>): StaffMember[] => response.data || [],
    }),
    createStaffMember: build.mutation<Response<StaffMember>, { businessId: string; eventId: string; data: CreateStaffMemberDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['staff_members'],
    }),
    updateStaffMember: build.mutation<Response<StaffMember>, { businessId: string; eventId: string; data: UpdateStaffMemberDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['staff_members'],
    }),
    deleteStaffMember: build.mutation<Response<null>, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['staff_members'],
    }),
    enableStaffMember: build.mutation<Response<StaffMember>, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}/enable`,
        method: 'POST',
      }),
      invalidatesTags: ['staff_members'],
    }),
    disableStaffMember: build.mutation<Response<StaffMember>, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}/disable`,
        method: 'POST',
      }),
      invalidatesTags: ['staff_members'],
    }),
    resendStaffMemberQr: build.mutation<Response<null>, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}/resend-qr`,
        method: 'POST',
      }),
    }),
    regenerateStaffMemberQr: build.mutation<Response<StaffMember>, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}/regenerate-qr`,
        method: 'POST',
      }),
      invalidatesTags: ['staff_members'],
    }),
    importStaffMembers: build.mutation<Response<StaffImportResult>, { businessId: string; eventId: string; file: FormData }>({
      query: ({ businessId, eventId, file }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/import`,
        method: 'POST',
        body: file,
      }),
      invalidatesTags: ['staff_members'],
    }),
    downloadStaffMemberQr: build.query<Blob, { businessId: string; eventId: string; memberId: string }>({
      query: ({ businessId, eventId, memberId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/members/${memberId}/qr`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // --- Doors ---
    getStaffDoors: build.query<StaffDoor[], { businessId: string; eventId: string }>({
      providesTags: ['staff_doors'],
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/doors`,
        method: 'GET',
      }),
      transformResponse: (response: Response<StaffDoor[]>): StaffDoor[] => response.data || [],
    }),
    createStaffDoor: build.mutation<Response<StaffDoor>, { businessId: string; eventId: string; data: CreateStaffDoorDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/doors`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['staff_doors'],
    }),
    updateStaffDoor: build.mutation<Response<StaffDoor>, { businessId: string; eventId: string; data: UpdateStaffDoorDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/doors/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['staff_doors'],
    }),
    deleteStaffDoor: build.mutation<Response<null>, { businessId: string; eventId: string; doorId: string }>({
      query: ({ businessId, eventId, doorId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/doors/${doorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['staff_doors'],
    }),
    duplicateStaffDoor: build.mutation<Response<StaffDoor>, { businessId: string; eventId: string; doorId: string }>({
      query: ({ businessId, eventId, doorId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/doors/${doorId}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['staff_doors'],
    }),

    // --- Reports ---
    getStaffReports: build.query<StaffScanRecord[], { businessId: string; eventId: string; filter?: StaffReportFilter }>({
      providesTags: ['staff_reports'],
      query: ({ businessId, eventId, filter }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/reports`,
        method: 'GET',
        params: filter,
      }),
      transformResponse: (response: Response<StaffScanRecord[]>): StaffScanRecord[] => response.data || [],
    }),
    exportStaffReports: build.mutation<Blob, { businessId: string; eventId: string; filter?: StaffReportFilter; format: 'csv' | 'xlsx' }>({
      query: ({
        businessId, eventId, filter, format,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/reports/export`,
        method: 'POST',
        body: { ...filter, format },
        responseHandler: (response) => response.blob(),
      }),
    }),

    // --- Registration link ---
    generateStaffLink: build.mutation<Response<StaffRegistrationLink>, { businessId: string; eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/registration-link`,
        method: 'POST',
      }),
      invalidatesTags: ['staff_links'],
    }),
    getActiveStaffLink: build.query<StaffRegistrationLink | null, { businessId: string; eventId: string }>({
      providesTags: ['staff_links'],
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/staff/registration-link`,
        method: 'GET',
      }),
      transformResponse: (response: Response<StaffRegistrationLink>): StaffRegistrationLink | null => response.data || null,
    }),

    // --- Public registration (no auth) ---
    validateStaffRegistrationToken: build.query<{ valid: boolean; event_name?: string }, { token: string }>({
      query: ({ token }) => ({
        url: `staff/register/${token}/validate`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ valid: boolean; event_name?: string }>): { valid: boolean; event_name?: string } => response.data || { valid: false },
    }),
    submitStaffRegistration: build.mutation<Response<null>, { token: string; data: StaffRegistrationFormDTO }>({
      query: ({ token, data }) => ({
        url: `staff/register/${token}`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetStaffRolesQuery,
  useCreateStaffRoleMutation,
  useUpdateStaffRoleMutation,
  useDeleteStaffRoleMutation,
  useReplicateStaffRolesMutation,
  useGetStaffMembersQuery,
  useCreateStaffMemberMutation,
  useUpdateStaffMemberMutation,
  useDeleteStaffMemberMutation,
  useEnableStaffMemberMutation,
  useDisableStaffMemberMutation,
  useResendStaffMemberQrMutation,
  useRegenerateStaffMemberQrMutation,
  useImportStaffMembersMutation,
  useGetStaffDoorsQuery,
  useCreateStaffDoorMutation,
  useUpdateStaffDoorMutation,
  useDeleteStaffDoorMutation,
  useDuplicateStaffDoorMutation,
  useGetStaffReportsQuery,
  useExportStaffReportsMutation,
  useGenerateStaffLinkMutation,
  useGetActiveStaffLinkQuery,
  useValidateStaffRegistrationTokenQuery,
  useSubmitStaffRegistrationMutation,
} = staffApi;
